/**
 * Компонент загрузки изображений с превью, drag-and-drop и прогрессом.
 * Используется для полей image_url в формах категорий и товаров.
 */
import React, { useRef, useState } from 'react';
import { Upload, Button, message, Progress, Space, Image } from 'antd';
import {
  DeleteOutlined,
  SwapOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import type { UploadProps, RcFile } from 'antd/es/upload';
import api from '../services/api';

interface ImageUploadProps {
  /** Текущий URL изображения (управляемое значение для Form.Item) */
  value?: string;
  /** Колбэк при изменении URL (вызывается Form.Item автоматически) */
  onChange?: (url: string) => void;
  /** Папка на сервере для загрузки (default: 'products') */
  folder?: string;
  /** Колбэк о начале/завершении загрузки — родитель блокирует Save пока true */
  onUploadingChange?: (uploading: boolean) => void;
  /** Внешняя блокировка (например, идёт загрузка в другом загрузчике той же формы) */
  disabled?: boolean;
}

/** Допустимые MIME-типы (совпадают с nginx/backend limits для /admin/upload) */
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
/** Максимальный размер файла в байтах (5 МБ) */
export const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

const ALLOWED_TYPES = ALLOWED_IMAGE_TYPES;
const MAX_SIZE = MAX_IMAGE_SIZE;

/** Безопасный текст ошибки загрузки — detail может быть строкой, массивом
 * FastAPI 422 ошибок или отсутствовать вовсе; никогда не рендерим объект. */
export function describeUploadError(err: any): string {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((e: any) => (typeof e === 'string' ? e : e?.msg || 'Ошибка валидации'))
      .join('; ');
  }
  if (typeof err?.message === 'string' && err.message) return err.message;
  return 'Ошибка загрузки изображения';
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  folder = 'products',
  onUploadingChange,
  disabled,
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressKnown, setProgressKnown] = useState(false);
  // Байты реально дошли до сервера (e.loaded >= e.total), но ответ ещё не
  // пришёл — сервер нормализует изображение. Отдельно от `progress`, чтобы
  // округление процента (98.6% → 99%) не выдавало "обработку" раньше времени.
  const [transferComplete, setTransferComplete] = useState(false);

  // Синхронный лок на случай нескольких файлов, выбранных почти одновременно
  // (drag-and-drop нескольких файлов, двойной выбор в диалоге) — проверка
  // state `uploading` внутри beforeUpload/customRequest не успевает
  // обновиться между двумя почти одновременными вызовами.
  const uploadingRef = useRef(false);

  const isBusy = uploading || !!disabled;

  /** Валидация файла перед загрузкой */
  const beforeUpload = (file: RcFile): boolean => {
    if (uploadingRef.current) {
      message.warning('Подождите, пока завершится текущая загрузка');
      return false;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      message.error('Допустимы только JPG, PNG и WebP изображения');
      return false;
    }
    if (file.size > MAX_SIZE) {
      message.error('Размер файла не должен превышать 5 МБ');
      return false;
    }
    return true;
  };

  /** Кастомная загрузка через axios (с прогрессом и авторизацией) */
  const customUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;

    // Повторная проверка синхронным ref прямо в начале customRequest:
    // beforeUpload мог пропустить файл до того, как первая загрузка успела
    // выставить ref (оба колбэка antd вызываются до какого-либо await).
    if (uploadingRef.current) {
      onError?.(new Error('Уже идёт другая загрузка'));
      return;
    }
    uploadingRef.current = true;

    const formData = new FormData();
    formData.append('file', file as File);

    setUploading(true);
    setProgress(0);
    setProgressKnown(false);
    setTransferComplete(false);
    onUploadingChange?.(true);

    try {
      const { data } = await api.post(
        `/admin/upload?folder=${folder}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            // Без известного total прогресс неизвестен — не подменяем его
            // ложным "почти готово".
            if (!e.total) {
              setProgressKnown(false);
              return;
            }
            const pct = Math.round((e.loaded * 100) / e.total);
            setProgressKnown(true);
            setProgress(Math.min(pct, 99));
            // "Обработка" показываем только когда байты ДЕЙСТВИТЕЛЬНО все
            // дошли (loaded >= total), а не когда округлённый процент
            // случайно достиг 99 при неполной передаче.
            setTransferComplete(e.loaded >= e.total);
          },
        },
      );

      const url: unknown = data?.url;
      if (typeof url !== 'string' || !url) {
        throw new Error('Сервер не вернул ссылку на изображение');
      }
      setProgress(100);
      setProgressKnown(true);
      onChange?.(url);
      onSuccess?.(data);
      message.success('Изображение загружено');
    } catch (err: any) {
      message.error(describeUploadError(err));
      onError?.(err);
    } finally {
      uploadingRef.current = false;
      setUploading(false);
      setTransferComplete(false);
      onUploadingChange?.(false);
    }
  };

  /** Удаление текущего изображения */
  const handleRemove = () => {
    onChange?.('');
  };

  // ── Если изображение уже загружено — показываем превью ──
  if (value) {
    return (
      <div style={styles.previewWrapper}>
        <Image
          src={value}
          width={150}
          height={150}
          style={{ objectFit: 'cover', borderRadius: 8 }}
          fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTUwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTUwIiBoZWlnaHQ9IjE1MCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9Ijc1IiB5PSI3NSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iIGZpbGw9IiNjY2MiIGZvbnQtc2l6ZT0iMTQiPk5vIGltYWdlPC90ZXh0Pjwvc3ZnPg=="
        />
        <Space style={{ marginTop: 8 }}>
          <Upload
            showUploadList={false}
            beforeUpload={beforeUpload}
            customRequest={customUpload}
            accept=".jpg,.jpeg,.png,.webp"
            disabled={isBusy}
          >
            <Button
              size="small"
              icon={<SwapOutlined />}
              loading={uploading}
              disabled={isBusy}
            >
              Заменить
            </Button>
          </Upload>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={handleRemove}
            disabled={isBusy}
          >
            Удалить
          </Button>
        </Space>
        {uploading && (
          <>
            <Progress
              percent={progressKnown ? progress : 0}
              status="active"
              size="small"
              style={{ width: 150, marginTop: 4 }}
            />
            <span style={{ fontSize: 11, color: '#999' }}>
              {transferComplete ? 'Обработка изображения…' : 'Загрузка...'}
            </span>
          </>
        )}
      </div>
    );
  }

  // ── Если изображения нет — показываем зону загрузки ──
  return (
    <div>
      <Upload.Dragger
        showUploadList={false}
        beforeUpload={beforeUpload}
        customRequest={customUpload}
        accept=".jpg,.jpeg,.png,.webp"
        disabled={isBusy}
        style={styles.dragger}
      >
        {uploading ? (
          <div style={{ padding: '16px 0' }}>
            <Progress percent={progressKnown ? progress : 0} status="active" size="small" style={{ width: 120 }} />
            <p style={{ margin: '8px 0 0', color: '#999', fontSize: 12 }}>
              {transferComplete ? 'Обработка изображения…' : 'Загрузка...'}
            </p>
          </div>
        ) : (
          <div style={{ padding: '16px 0' }}>
            <p style={{ fontSize: 28, color: '#999', margin: 0 }}>
              <InboxOutlined />
            </p>
            <p style={{ margin: '8px 0 0', fontSize: 13 }}>
              Перетащите изображение или нажмите для выбора
            </p>
            <p style={{ margin: '4px 0 0', color: '#999', fontSize: 11 }}>
              JPG, PNG, WebP · до 5 МБ
            </p>
          </div>
        )}
      </Upload.Dragger>
    </div>
  );
};

/** Инлайн-стили компонента */
const styles: Record<string, React.CSSProperties> = {
  previewWrapper: {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  dragger: {
    width: 200,
    borderRadius: 8,
  },
};

export default ImageUpload;
